import { Link, useRouterState } from "@tanstack/react-router";
import { ArrowRight, ChevronDown, Github, Menu, X } from "lucide-react";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
	AGENT_ACCENTS,
	AGENT_ICONS,
	type AgentKey,
} from "@/components/brand/ai-agent-icons";
import { Logo } from "@/components/brand/logo";
import {
	PLATFORM_COLORS,
	PLATFORM_ICONS,
	type PlatformKey,
} from "@/components/platforms/platform-icons";
import { Reveal } from "@/components/reveal";
import { AnimatedThemeToggler } from "@/components/ui/animated-theme-toggler";
import { useSession } from "@/hooks/auth-hooks";
import { SUPPORT_EMAIL } from "@/lib/constants";
import { buildBreadcrumbJsonLd, buildCoreJsonLd, JsonLd } from "@/lib/json-ld";
import { useTheme } from "@/utils/themeProvider";

const WHATSAPP_COMMUNITY_URL =
	"https://chat.whatsapp.com/FRn4t6QZUVqI9Hctw423Um?mode=gi_t";

const subscribeToHydration = () => () => {};

const CHANNEL_LINKS = [
	{
		label: "Instagram",
		platform: "instagram",
		description: "Plan captions, visuals, Reels, and carousels.",
		to: "/channels/instagram",
	},
	{
		label: "Facebook",
		platform: "facebook",
		description: "Publish page posts alongside your other channels.",
		to: "/channels/facebook",
	},
	{
		label: "LinkedIn",
		platform: "linkedin",
		description: "Schedule company, founder, and thought-leadership posts.",
		to: "/channels/linkedin",
	},
	{
		label: "X (Twitter)",
		platform: "x",
		description: "Queue threads, replies, and daily updates for X.",
		to: "/channels/x",
	},
	{
		label: "TikTok",
		platform: "tiktok",
		description: "Schedule short video concepts and posts for TikTok.",
		to: "/channels/tiktok",
	},
	{
		label: "YouTube",
		platform: "youtube",
		description: "Organize Shorts, videos, and launch updates.",
		to: "/channels/youtube",
	},
	{
		label: "Pinterest",
		platform: "pinterest",
		description: "Schedule pins and evergreen campaign content.",
		to: "/channels/pinterest",
	},
	{
		label: "Threads",
		platform: "threads",
		description: "Turn ideas into native Threads updates.",
		to: "/channels/threads",
	},
	{
		label: "Bluesky",
		platform: "bluesky",
		description: "Publish short-form posts to Bluesky from chat.",
		to: "/channels/bluesky",
	},
] satisfies Array<{
	label: string;
	platform: PlatformKey;
	description: string;
	to: string;
}>;

const AGENT_LINKS = [
	{
		label: "Claude",
		agent: "claude",
		description: "Connect Claude, the Claude API, or Claude Code CLI.",
		to: "/claude-social-media",
	},
	{
		label: "OpenClaw",
		agent: "openclaw",
		description: "Open-source agents that actually post.",
		to: "/openclaw-social-media",
	},
	{
		label: "ChatGPT",
		agent: "chatgpt",
		description: "Turn ChatGPT into a publishing operator.",
		to: "/chatgpt-social-media",
	},
	{
		label: "Cursor",
		agent: "cursor",
		description: "Draft and queue posts next to the diff that shipped.",
		to: "/cursor-social-media",
	},
	{
		label: "Gemini",
		agent: "gemini",
		description: "Connect Gemini to AgentReacher via MCP.",
		to: "/gemini-social-media",
	},
] satisfies Array<{
	label: string;
	agent: AgentKey;
	description: string;
	to: string;
}>;

export function PublicHeader() {
	const { user, isPending } = useSession();
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const [scrolled, setScrolled] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const previousPathnameRef = useRef(pathname);
	const breadcrumbJsonLd = buildBreadcrumbJsonLd(pathname);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 10);
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	useEffect(() => {
		if (!mobileMenuOpen) return;

		const scrollY = window.scrollY;
		const { style } = document.body;
		const previousBodyStyles = {
			position: style.position,
			top: style.top,
			width: style.width,
			overflow: style.overflow,
		};
		const previousHtmlOverflow = document.documentElement.style.overflow;

		document.documentElement.style.overflow = "hidden";
		style.position = "fixed";
		style.top = `-${scrollY}px`;
		style.width = "100%";
		style.overflow = "hidden";

		return () => {
			document.documentElement.style.overflow = previousHtmlOverflow;
			style.position = previousBodyStyles.position;
			style.top = previousBodyStyles.top;
			style.width = previousBodyStyles.width;
			style.overflow = previousBodyStyles.overflow;
			window.scrollTo(0, scrollY);
		};
	}, [mobileMenuOpen]);

	useEffect(() => {
		if (previousPathnameRef.current !== pathname) {
			previousPathnameRef.current = pathname;
			setMobileMenuOpen(false);
		}
	}, [pathname]);

	return (
		<>
			<JsonLd data={buildCoreJsonLd()} />
			{breadcrumbJsonLd ? <JsonLd data={breadcrumbJsonLd} /> : null}
			<header className="sticky top-0 z-50">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="public-header-stack flex flex-col divide-y divide-border">
						<div
							className={`flex items-center justify-between rounded-md px-4 py-3 sm:px-6 sm:py-4 md:grid md:grid-cols-[1fr_auto_1fr] ${
								scrolled ? "bg-background/80 backdrop-blur-md" : "bg-background"
							}`}
						>
							<div className="flex items-center">
								<Link to="/" className="flex items-center self-center">
									<Logo
										size="sm"
										disableLink
										textClassName="hidden min-[400px]:inline"
									/>
								</Link>
							</div>
							<nav className="hidden items-center gap-1 md:flex">
								<div className="group relative">
									<button
										type="button"
										className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground group-focus-within:bg-accent group-focus-within:text-foreground"
									>
										Channels
										<ChevronDown className="size-3.5 transition-transform group-hover:rotate-180 group-focus-within:rotate-180" />
									</button>
									<div className="invisible absolute left-1/2 top-full z-50 w-[42rem] -translate-x-1/2 pt-4 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
										<div className="rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-xl">
											<div className="grid grid-cols-3 gap-1">
												{CHANNEL_LINKS.map((link) => (
													<Link
														key={link.to}
														to={link.to}
														className="flex gap-3 rounded-lg p-3 transition-colors hover:bg-accent focus:bg-accent focus:outline-none"
													>
														<span
															aria-hidden="true"
															className={`flex size-8 shrink-0 items-center justify-center rounded-full ${PLATFORM_COLORS[link.platform].bg}`}
														>
															{PLATFORM_ICONS[link.platform](
																`size-4 ${PLATFORM_COLORS[link.platform].text}`,
															)}
														</span>
														<span className="min-w-0">
															<span className="block text-sm font-semibold text-foreground">
																{link.label}
															</span>
															<span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
																{link.description}
															</span>
														</span>
													</Link>
												))}
											</div>
										</div>
									</div>
								</div>
								<div className="group relative">
									<button
										type="button"
										className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground group-focus-within:bg-accent group-focus-within:text-foreground"
									>
										AI Agents
										<ChevronDown className="size-3.5 transition-transform group-hover:rotate-180 group-focus-within:rotate-180" />
									</button>
									<div className="invisible absolute left-1/2 top-full z-50 w-[44rem] -translate-x-1/2 pt-4 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
										<div className="rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-xl">
											<Link
												to="/agent"
												className="mb-1 flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3 transition-colors hover:bg-accent focus:bg-accent focus:outline-none"
											>
												<span>
													<span className="block text-sm font-semibold text-foreground">
														AgentReacher CLI + skills
													</span>
													<span className="mt-1 block text-xs text-muted-foreground">
														Install the publishing workflow into any AI agent.
													</span>
												</span>
												<ArrowRight className="size-4 text-primary" />
											</Link>
											<div className="grid grid-cols-2 gap-1">
												{AGENT_LINKS.map((link) => (
													<Link
														key={link.to}
														to={link.to}
														className="flex gap-3 rounded-lg p-3 transition-colors hover:bg-accent focus:bg-accent focus:outline-none"
													>
														<span
															aria-hidden="true"
															className={`flex size-8 shrink-0 items-center justify-center rounded-full ${AGENT_ACCENTS[link.agent].bg}`}
														>
															{AGENT_ICONS[link.agent](
																`size-4 ${AGENT_ACCENTS[link.agent].text}`,
															)}
														</span>
														<span className="min-w-0">
															<span className="block text-sm font-semibold text-foreground">
																{link.label}
															</span>
															<span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
																{link.description}
															</span>
														</span>
													</Link>
												))}
											</div>
										</div>
									</div>
								</div>
								<Link
									to="/pricing"
									className="rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
								>
									Pricing
								</Link>
								<Link
									to="/webinar"
									className="rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
								>
									Webinar
								</Link>
							</nav>
							<div className="flex min-h-8 min-w-[10rem] items-center justify-end gap-2 sm:min-w-[13rem] md:min-w-0">
								{isPending ? (
									<>
										<div
											aria-hidden="true"
											className="hidden h-8 w-[3.5rem] rounded-md sm:block"
										/>
										<div
											aria-hidden="true"
											className="h-8 w-[5.375rem] rounded-md"
										/>
									</>
								) : user ? (
									<Link
										to="/dashboard"
										className="inline-flex h-8 items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
									>
										Dashboard
									</Link>
								) : (
									<>
										<Link
											to="/login"
											className="hidden h-8 items-center rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent sm:inline-flex"
										>
											Log in
										</Link>
										<Link
											to="/choose-plan"
											search={{ redirect: undefined, reason: undefined }}
											className="inline-flex h-8 items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
										>
											Start now
										</Link>
									</>
								)}
								<AnimatedThemeToggler
									className="hidden md:inline-flex"
									size="sm"
								/>
								<button
									type="button"
									onClick={() => setMobileMenuOpen((open) => !open)}
									aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
									aria-expanded={mobileMenuOpen}
									className="inline-flex size-8 items-center justify-center rounded-md border border-border bg-background text-foreground shadow-sm transition-colors hover:bg-accent md:hidden"
								>
									{mobileMenuOpen ? (
										<X className="size-4" />
									) : (
										<Menu className="size-4" />
									)}
								</button>
							</div>
						</div>
						{mobileMenuOpen && (
							<nav className="mt-px grid max-h-[calc(100svh-4.5rem)] gap-px overflow-y-auto overscroll-contain bg-border md:hidden">
								{!isPending && !user ? (
									<div className="rounded-md bg-background px-4 py-3">
										<Link
											to="/login"
											onClick={() => setMobileMenuOpen(false)}
											className="inline-flex min-h-10 w-full items-center justify-center rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent"
										>
											Log in
										</Link>
									</div>
								) : null}
								<Link
									to="/agent"
									onClick={() => setMobileMenuOpen(false)}
									className="rounded-md bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-accent"
								>
									<div className="flex items-center gap-2">
										<ArrowRight className="size-4 text-primary" />
										<span>Agent CLI + workflows</span>
									</div>
								</Link>
								<Link
									to="/skills"
									onClick={() => setMobileMenuOpen(false)}
									className="rounded-md bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-accent"
								>
									<div className="flex items-center gap-2">
										<Github className="size-4 text-primary" />
										<span>Open-source skills</span>
									</div>
								</Link>
								<Link
									to="/pricing"
									onClick={() => setMobileMenuOpen(false)}
									className="rounded-md bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-accent"
								>
									Pricing
								</Link>
								<Link
									to="/webinar"
									onClick={() => setMobileMenuOpen(false)}
									className="rounded-md bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-accent"
								>
									Webinar
								</Link>
								<div className="rounded-md bg-background px-4 py-3">
									<p className="text-sm font-medium text-foreground">
										Channels
									</p>
									<div className="mt-2 grid grid-cols-2 gap-2">
										{CHANNEL_LINKS.map((link) => (
											<Link
												key={link.to}
												to={link.to}
												onClick={() => setMobileMenuOpen(false)}
												className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
											>
												<span
													aria-hidden="true"
													className={`flex size-6 shrink-0 items-center justify-center rounded-full ${PLATFORM_COLORS[link.platform].bg}`}
												>
													{PLATFORM_ICONS[link.platform](
														`size-3.5 ${PLATFORM_COLORS[link.platform].text}`,
													)}
												</span>
												<span className="truncate">{link.label}</span>
											</Link>
										))}
									</div>
								</div>
								<div className="rounded-md bg-background px-4 py-3">
									<p className="text-sm font-medium text-foreground">
										AI Agents
									</p>
									<div className="mt-2 grid grid-cols-2 gap-2">
										{AGENT_LINKS.map((link) => (
											<Link
												key={link.to}
												to={link.to}
												onClick={() => setMobileMenuOpen(false)}
												className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
											>
												<span
													aria-hidden="true"
													className={`flex size-6 shrink-0 items-center justify-center rounded-full ${AGENT_ACCENTS[link.agent].bg}`}
												>
													{AGENT_ICONS[link.agent](
														`size-3.5 ${AGENT_ACCENTS[link.agent].text}`,
													)}
												</span>
												<span className="truncate">{link.label}</span>
											</Link>
										))}
									</div>
								</div>
							</nav>
						)}
					</div>
				</div>
			</header>
		</>
	);
}

export function PublicFooter({ showCta = true }: { showCta?: boolean } = {}) {
	return (
		<footer>
			<div className="mx-auto max-w-6xl px-4 sm:px-6">
				<div className="public-footer-stack flex flex-col overflow-hidden divide-y divide-border">
					{showCta && <FooterCta />}
					<Reveal className="grid grid-cols-2 gap-8 rounded-md bg-white dark:bg-black px-6 py-12 sm:grid-cols-3 sm:px-8 lg:grid-cols-5">
						<div className="col-span-2 space-y-4 sm:col-span-3 lg:col-span-1">
							<Logo size="sm" disableLink />
							<p className="max-w-[22ch] text-sm text-muted-foreground">
								Social scheduling for AI agents.
							</p>
							<p className="max-w-[26ch] text-xs text-muted-foreground">
								From the maker of{" "}
								<a
									href="https://brandreel.com"
									target="_blank"
									rel="noopener noreferrer"
									className="text-foreground underline underline-offset-2 transition-colors hover:text-muted-foreground"
								>
									BrandReel
								</a>{" "}
								and{" "}
								<a
									href="https://agentmrr.com"
									target="_blank"
									rel="noopener noreferrer"
									className="text-foreground underline underline-offset-2 transition-colors hover:text-muted-foreground"
								>
									AgentMRR
								</a>
								.
							</p>
						</div>

						<FooterColumn
							title="Product"
							links={[
								{ label: "Log in", to: "/login" },
								{ label: "Pricing", to: "/pricing" },
								{ label: "Webinar", to: "/webinar" },
								{ label: "Affiliates", to: "/affiliate-program" },
								{ label: "Agent CLI", to: "/agent" },
								{ label: "Skills", to: "/skills" },
								{ label: "MCP", to: "/docs/mcp" },
								{ label: "Docs", to: "/docs" },
								{ label: "Blog", to: "/blog" },
								{ label: "WhatsApp Community", href: WHATSAPP_COMMUNITY_URL },
								{ label: "Support", to: "/support" },
							]}
						/>
						<FooterColumn
							title="Channels"
							links={[
								{ label: "Instagram", to: "/channels/instagram" },
								{ label: "Facebook", to: "/channels/facebook" },
								{ label: "LinkedIn", to: "/channels/linkedin" },
								{ label: "X (Twitter)", to: "/channels/x" },
								{ label: "TikTok", to: "/channels/tiktok" },
								{ label: "YouTube", to: "/channels/youtube" },
								{ label: "Pinterest", to: "/channels/pinterest" },
								{ label: "Threads", to: "/channels/threads" },
								{ label: "Bluesky", to: "/channels/bluesky" },
							]}
						/>
						<FooterColumn
							title="Solutions"
							links={[
								{ label: "Founders", to: "/for/founders" },
								{ label: "Agencies", to: "/for/agencies" },
								{ label: "Creators", to: "/for/creators" },
								{ label: "B2B SaaS", to: "/for/b2b-saas" },
								{ label: "Ecommerce", to: "/for/ecommerce" },
								{ label: "vs. Buffer", to: "/alternatives/buffer" },
								{ label: "vs. Hootsuite", to: "/alternatives/hootsuite" },
								{ label: "vs. Postiz", to: "/alternatives/postiz" },
							]}
						/>
						<FooterColumn
							title="AI Agents"
							links={[
								{ label: "Claude", to: "/claude-social-media" },
								{ label: "OpenClaw", to: "/openclaw-social-media" },
								{ label: "Claude Code", to: "/claude-code-social-media" },
								{ label: "ChatGPT", to: "/chatgpt-social-media" },
								{ label: "Cursor", to: "/cursor-social-media" },
								{ label: "Codex", to: "/codex-social-media" },
								{ label: "Gemini", to: "/gemini-social-media" },
							]}
						/>
					</Reveal>
					<div className="flex flex-col gap-4 rounded-md bg-white dark:bg-black px-6 py-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-8">
						<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
							<span>
								&copy; {new Date().getFullYear()} AgentReacher. All rights
								reserved.
							</span>
							<a
								href="https://verifieddr.com/website/agentreacher-com"
								target="_blank"
								rel="noreferrer"
							>
								<img
									src="https://verifieddr.com/badge/agentreacher-com.svg"
									alt="Verified DR - Verified Domain Rating for agentreacher.com"
									width={220}
									height={68}
									className="block dark:hidden"
								/>
								<img
									src="https://verifieddr.com/badge/agentreacher-com-dark.svg"
									alt="Verified DR - Verified Domain Rating for agentreacher.com"
									width={220}
									height={68}
									className="hidden dark:block"
								/>
							</a>
						</div>
						<div className="flex flex-wrap items-center gap-4">
							<Link
								to="/terms"
								className="transition-colors hover:text-foreground"
							>
								Terms
							</Link>
							<Link
								to="/privacy"
								className="transition-colors hover:text-foreground"
							>
								Privacy
							</Link>
							<a
								href={`mailto:${SUPPORT_EMAIL}`}
								className="transition-colors hover:text-foreground"
							>
								Email
							</a>
							<div className="flex items-center gap-2">
								<ActiveThemeLabel />
								<AnimatedThemeToggler size="sm" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
}

function ActiveThemeLabel() {
	const { theme } = useTheme();
	const mounted = useSyncExternalStore(
		subscribeToHydration,
		() => true,
		() => false,
	);
	const label =
		mounted && theme ? `${theme[0]?.toUpperCase()}${theme.slice(1)}` : "Theme";
	return <span>{label}</span>;
}

function FooterColumn({
	title,
	links,
}: {
	title: string;
	links: Array<{ label: string; to: string } | { label: string; href: string }>;
}) {
	return (
		<div className="space-y-3 text-sm">
			<p className="text-sm font-medium text-foreground">{title}</p>
			<div className="flex flex-col gap-2">
				{links.map((link) =>
					"href" in link ? (
						<a
							key={link.href + link.label}
							href={link.href}
							className="text-muted-foreground transition-colors hover:text-foreground"
						>
							{link.label}
						</a>
					) : (
						<Link
							key={link.to + link.label}
							to={link.to}
							className="text-muted-foreground transition-colors hover:text-foreground"
						>
							{link.label}
						</Link>
					),
				)}
			</div>
		</div>
	);
}

const FOOTER_CTA_NETWORKS: PlatformKey[] = [
	"linkedin",
	"instagram",
	"x",
	"tiktok",
	"youtube",
	"threads",
	"facebook",
	"bluesky",
	"pinterest",
];

function FooterCta() {
	const [visible, setVisible] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const node = ref.current;
		if (!node) return undefined;
		if (typeof IntersectionObserver === "undefined") {
			setVisible(true);
			return undefined;
		}
		const observer = new IntersectionObserver(
			([entry]) => setVisible(Boolean(entry?.isIntersecting)),
			{ threshold: 0 },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, []);

	return (
		<Reveal>
			<div
				ref={ref}
				data-animate={visible ? "true" : undefined}
				className="relative overflow-hidden rounded-md bg-card px-6 py-12 text-foreground sm:px-12 sm:py-16"
			>
				<div
					aria-hidden="true"
					className="pointer-events-none absolute inset-0 text-foreground/10 opacity-60"
					style={{
						backgroundImage:
							"radial-gradient(currentColor 1px, transparent 1px)",
						backgroundSize: "16px 16px",
					}}
				/>
				<div
					aria-hidden="true"
					className="pointer-events-none absolute inset-0 bg-gradient-to-br from-background/0 via-background/40 to-background/80"
				/>
				<div className="relative grid grid-cols-1 items-center gap-10 sm:grid-cols-[1.15fr_1fr] sm:gap-12">
					<div className="max-w-lg">
						<div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
							<span className="size-1.5 rounded-full bg-emerald-500" />
							Turn plans into replies
						</div>
						<h2 className="mt-5 text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
							Your audience is closer
							<br />
							<span className="text-primary">than you think.</span>
						</h2>
						<p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
							Your ideas deserve more than silence. AgentReacher helps you
							publish, respond, and build real momentum.
						</p>
						<div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
							<Link
								to="/choose-plan"
								search={{ redirect: undefined, reason: undefined }}
								className="group inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-sm transition-all hover:bg-primary/90 hover:shadow-md"
							>
								Start now
								<ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
							</Link>
							<Link
								to="/pricing"
								className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
							>
								See pricing
								<ArrowRight className="size-3.5" />
							</Link>
						</div>
					</div>
					<div className="hidden sm:block">
						<div className="grid grid-cols-3 gap-2">
							{FOOTER_CTA_NETWORKS.map((p) => (
								<Link
									key={p}
									to={`/channels/${p}`}
									aria-label={`${p} channel`}
									className={`flex aspect-square items-center justify-center rounded-md border border-border bg-background/80 backdrop-blur transition-colors hover:bg-background ${PLATFORM_COLORS[p].text}`}
								>
									{PLATFORM_ICONS[p]("size-7")}
								</Link>
							))}
						</div>
					</div>
				</div>
			</div>
		</Reveal>
	);
}
